/**
 * Campaigns Management Page
 *
 * Enterprise features:
 * - Real-time campaign stats with polling
 * - Campaign CRUD operations
 * - Status management (start/pause/stop)
 * - ROAS and budget tracking
 * - Campaign detail modal
 */

import { useState, useMemo, useCallback } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import Modal from '../../components/modals/Modal';
import { campaignsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './LazyMarketing.css';

// Constants
const STATS_REFRESH_INTERVAL = 30000; // 30 seconds

const Campaigns = () => {
  const { showToast } = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    budget: '',
    type: 'awareness',
  });

  // Fetch campaigns with stats
  const {
    data: campaignsData,
    isLoading: loadingCampaigns,
    refetch: refetchCampaigns,
  } = useQuery(
    () => campaignsApi.list({
      status: statusFilter === 'all' ? undefined : statusFilter,
      includeStats: true,
    }),
    {
      key: `campaigns:list:${statusFilter}`,
      cacheTTL: 15000,
      refetchInterval: STATS_REFRESH_INTERVAL,
    }
  );

  // Create campaign mutation
  const createMutation = useMutation(
    (data) => campaignsApi.create(data),
    {
      onSuccess: () => {
        showToast('Campaign created', 'success');
        refetchCampaigns();
        setShowCreateModal(false);
        setNewCampaign({ name: '', budget: '', type: 'awareness' });
      },
      onError: (error) => {
        showToast(error.message || 'Failed to create campaign', 'error');
      },
    }
  );

  // Update campaign mutation
  const updateMutation = useMutation(
    ({ id, data }) => campaignsApi.update(id, data),
    {
      onSuccess: () => {
        showToast('Campaign updated', 'success');
        refetchCampaigns();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to update campaign', 'error');
      },
    }
  );

  // Start/Pause/Stop mutations
  const startMutation = useMutation(
    (id) => campaignsApi.start(id),
    {
      onSuccess: () => {
        showToast('Campaign started', 'success');
        refetchCampaigns();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to start campaign', 'error');
      },
    }
  );

  const pauseMutation = useMutation(
    (id) => campaignsApi.pause(id),
    {
      onSuccess: () => {
        showToast('Campaign paused', 'success');
        refetchCampaigns();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to pause campaign', 'error');
      },
    }
  );

  const stopMutation = useMutation(
    (id) => campaignsApi.stop(id),
    {
      onSuccess: () => {
        showToast('Campaign stopped', 'success');
        refetchCampaigns();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to stop campaign', 'error');
      },
    }
  );

  // Compute stats
  const stats = useMemo(() => {
    const data = campaignsData || {};
    const meta = data.stats || data.meta || {};
    const campaigns = data.campaigns || data.data || [];

    const activeCampaigns = campaigns.filter(c => c.status === 'Active' || c.status === 'active').length;
    const totalBudget = campaigns.reduce((sum, c) => sum + (parseFloat(c.budget) || 0), 0);
    const avgRoas = campaigns.length > 0
      ? (campaigns.reduce((sum, c) => sum + (parseFloat(c.roas) || 0), 0) / campaigns.length).toFixed(1)
      : 0;
    const totalConversions = campaigns.reduce((sum, c) => sum + (c.conversions || 0), 0);

    return [
      {
        label: 'Active Campaigns',
        value: meta.active || activeCampaigns,
        change: formatChange(meta.activeChange),
        changeType: getChangeType(meta.activeChange),
        icon: 'play-circle',
      },
      {
        label: 'Total Budget',
        value: formatCurrency(meta.totalBudget || totalBudget),
        change: formatChange(meta.budgetChange),
        changeType: getChangeType(meta.budgetChange),
        icon: 'dollar-sign',
      },
      {
        label: 'Avg. ROAS',
        value: `${meta.avgRoas || avgRoas}x`,
        change: formatChange(meta.roasChange, 'x'),
        changeType: getChangeType(meta.roasChange),
        icon: 'trending-up',
      },
      {
        label: 'Conversions',
        value: formatNumber(meta.conversions || totalConversions),
        change: formatChange(meta.conversionsChange, '%'),
        changeType: getChangeType(meta.conversionsChange),
        icon: 'target',
      },
    ];
  }, [campaignsData]);

  // Extract campaigns list
  const campaignsList = useMemo(() => {
    const data = campaignsData || {};
    return data.campaigns || data.data || [];
  }, [campaignsData]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchCampaigns();
    showToast('Campaigns refreshed', 'success');
  }, [refetchCampaigns, showToast]);

  const handleCampaignClick = useCallback((campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
  }, []);

  const handleStatusAction = useCallback((campaignId, action) => {
    switch (action) {
      case 'start':
        startMutation.mutate(campaignId);
        break;
      case 'pause':
        pauseMutation.mutate(campaignId);
        break;
      case 'stop':
        stopMutation.mutate(campaignId);
        break;
      default:
        break;
    }
  }, [startMutation, pauseMutation, stopMutation]);

  const handleCreateCampaign = useCallback(() => {
    if (!newCampaign.name || !newCampaign.budget) {
      showToast('Please fill in all fields', 'error');
      return;
    }
    createMutation.mutate({
      name: newCampaign.name,
      budget: parseFloat(newCampaign.budget),
      type: newCampaign.type,
      status: 'draft',
    });
  }, [newCampaign, createMutation, showToast]);

  // Helpers
  function formatCurrency(amount) {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(amount >= 10000 ? 0 : 1)}K`;
    return `$${amount.toLocaleString()}`;
  }

  function formatNumber(num) {
    if (!num) return '0';
    return num.toLocaleString();
  }

  function formatChange(value, suffix = '') {
    if (value === null || value === undefined) return undefined;
    return `${value >= 0 ? '+' : ''}${value}${suffix}`;
  }

  function getChangeType(value) {
    if (value === null || value === undefined) return 'neutral';
    return value >= 0 ? 'positive' : 'negative';
  }

  function getStatusStyle(status) {
    switch (status?.toLowerCase()) {
      case 'active':
        return { background: 'var(--success-soft)', color: 'var(--success)' };
      case 'paused':
        return { background: 'var(--warning-soft)', color: 'var(--warning)' };
      case 'stopped':
      case 'draft':
        return { background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' };
      case 'error':
        return { background: 'var(--danger-soft)', color: 'var(--danger)' };
      default:
        return { background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' };
    }
  }

  function getRoasColor(roas) {
    const value = parseFloat(roas) || 0;
    if (value >= 4) return 'var(--success)';
    if (value >= 2) return 'var(--warning)';
    return 'var(--danger)';
  }

  const isLoading = loadingCampaigns && campaignsList.length === 0;

  return (
    <div className="lm-page">
      {/* Header */}
      <div className="lm-page-header">
        <div>
          <h1 className="lm-page-title">Campaigns</h1>
          <p className="lm-page-subtitle">Manage your marketing campaigns</p>
        </div>
        <div className="lm-page-actions">
          <Button variant="secondary" icon="refresh" onClick={handleRefresh} disabled={loadingCampaigns}>
            Refresh
          </Button>
          <Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
            New Campaign
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="lm-stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loadingCampaigns && !campaignsData} />
        ))}
      </div>

      {/* Filters */}
      <div className="lm-filters">
        <div className="filter-tabs">
          {['all', 'active', 'paused', 'draft', 'stopped'].map((filter) => (
            <button
              key={filter}
              className={`filter-tab ${statusFilter === filter ? 'active' : ''}`}
              onClick={() => setStatusFilter(filter)}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Campaigns Grid */}
      <div className="lm-section">
        <div className="lm-section-header">
          <h3 className="lm-section-title">Campaign List</h3>
          <span className="lm-section-count">
            {loadingCampaigns ? '...' : `${campaignsList.length} campaigns`}
          </span>
        </div>

        {isLoading ? (
          <div className="lm-grid">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="lm-grid-card skeleton">
                <div className="skeleton-header">
                  <div className="skeleton-line" style={{ width: '70%' }}></div>
                  <div className="skeleton-badge"></div>
                </div>
                <div className="skeleton-content">
                  <div className="skeleton-line" style={{ width: '60%' }}></div>
                </div>
              </div>
            ))}
          </div>
        ) : campaignsList.length > 0 ? (
          <div className="lm-grid">
            {campaignsList.map((campaign) => (
              <div
                key={campaign.id}
                className="lm-grid-card clickable"
                onClick={() => handleCampaignClick(campaign)}
              >
                <div className="lm-grid-card-header">
                  <span className="lm-grid-card-title">{campaign.name}</span>
                  <span className="lm-grid-card-badge" style={getStatusStyle(campaign.status)}>
                    {campaign.status}
                  </span>
                </div>
                <div className="lm-grid-card-content">
                  <div className="campaign-metrics">
                    <div className="campaign-metric">
                      <span className="metric-label">Budget</span>
                      <span className="metric-value">{formatCurrency(campaign.budget)}</span>
                    </div>
                    <div className="campaign-metric">
                      <span className="metric-label">ROAS</span>
                      <span className="metric-value" style={{ color: getRoasColor(campaign.roas) }}>
                        {campaign.roas || '0'}x
                      </span>
                    </div>
                    <div className="campaign-metric">
                      <span className="metric-label">Conversions</span>
                      <span className="metric-value">{formatNumber(campaign.conversions)}</span>
                    </div>
                  </div>
                </div>
                <div className="lm-grid-card-footer">
                  <span className="campaign-type">{campaign.type || 'General'}</span>
                  <div className="campaign-actions">
                    {campaign.status?.toLowerCase() === 'active' ? (
                      <button
                        className="action-btn pause"
                        onClick={(e) => { e.stopPropagation(); handleStatusAction(campaign.id, 'pause'); }}
                        title="Pause"
                      >
                        ⏸
                      </button>
                    ) : campaign.status?.toLowerCase() !== 'stopped' ? (
                      <button
                        className="action-btn start"
                        onClick={(e) => { e.stopPropagation(); handleStatusAction(campaign.id, 'start'); }}
                        title="Start"
                      >
                        ▶
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="lm-empty">
            <Icon name="target" size="xl" />
            <p>No campaigns found</p>
            <Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
              Create your first campaign
            </Button>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <Modal
          title="Create Campaign"
          onClose={() => setShowCreateModal(false)}
        >
          <div className="create-campaign-form">
            <div className="form-group">
              <label className="form-label">Campaign Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter campaign name"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Budget ($)</label>
              <input
                type="number"
                className="form-input"
                placeholder="Enter budget"
                value={newCampaign.budget}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, budget: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Campaign Type</label>
              <select
                className="form-select"
                value={newCampaign.type}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="awareness">Brand Awareness</option>
                <option value="conversion">Conversion</option>
                <option value="retargeting">Retargeting</option>
                <option value="lead-gen">Lead Generation</option>
              </select>
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateCampaign}
                disabled={createMutation.isLoading}
              >
                {createMutation.isLoading ? 'Creating...' : 'Create Campaign'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Campaign Detail Modal */}
      {showDetailModal && selectedCampaign && (
        <Modal
          title="Campaign Details"
          onClose={() => setShowDetailModal(false)}
        >
          <div className="campaign-detail-modal">
            <div className="campaign-detail-header">
              <h3>{selectedCampaign.name}</h3>
              <span className="campaign-status" style={getStatusStyle(selectedCampaign.status)}>
                {selectedCampaign.status}
              </span>
            </div>

            <div className="campaign-detail-stats">
              <div className="detail-stat">
                <span className="stat-value">{formatCurrency(selectedCampaign.budget)}</span>
                <span className="stat-label">Budget</span>
              </div>
              <div className="detail-stat">
                <span className="stat-value" style={{ color: getRoasColor(selectedCampaign.roas) }}>
                  {selectedCampaign.roas || '0'}x
                </span>
                <span className="stat-label">ROAS</span>
              </div>
              <div className="detail-stat">
                <span className="stat-value">{formatNumber(selectedCampaign.conversions)}</span>
                <span className="stat-label">Conversions</span>
              </div>
              <div className="detail-stat">
                <span className="stat-value">{formatNumber(selectedCampaign.clicks)}</span>
                <span className="stat-label">Clicks</span>
              </div>
            </div>

            <div className="campaign-detail-section">
              <h4>Campaign Info</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Type</span>
                  <span className="detail-value">{selectedCampaign.type || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Channel</span>
                  <span className="detail-value">{selectedCampaign.channel || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Start Date</span>
                  <span className="detail-value">
                    {selectedCampaign.startDate ? new Date(selectedCampaign.startDate).toLocaleDateString() : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">End Date</span>
                  <span className="detail-value">
                    {selectedCampaign.endDate ? new Date(selectedCampaign.endDate).toLocaleDateString() : 'Ongoing'}
                  </span>
                </div>
              </div>
            </div>

            <div className="campaign-detail-actions">
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              {selectedCampaign.status?.toLowerCase() === 'active' ? (
                <Button
                  variant="warning"
                  onClick={() => handleStatusAction(selectedCampaign.id, 'pause')}
                  disabled={pauseMutation.isLoading}
                >
                  Pause Campaign
                </Button>
              ) : selectedCampaign.status?.toLowerCase() !== 'stopped' ? (
                <Button
                  variant="primary"
                  onClick={() => handleStatusAction(selectedCampaign.id, 'start')}
                  disabled={startMutation.isLoading}
                >
                  Start Campaign
                </Button>
              ) : null}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Campaigns;
